import React, { useEffect, useState } from "react";
import "./App.css";
import { auth, db } from "./firebase";
import {
  GoogleAuthProvider,
  signInWithPopup,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import ReCAPTCHA from "react-google-recaptcha";
import { APPS } from "./apps";

const RECAPTCHA_SITE_KEY = "6Le1c8UsAAAAAIAtrbD_2xcUm1qeJnbujpj6KXDA";

function todayUtcYmd() {
  return new Date().toISOString().slice(0, 10).replace(/-/g, "");
}

function formatError(err) {
  const code = err?.code || "";
  if (code === "permission-denied") {
    return "A request for this app was already submitted today. Additional submissions aren't needed — we'll process the existing one.";
  }
  if (code === "auth/popup-closed-by-user") {
    return "Sign-in was cancelled.";
  }
  if (code === "auth/popup-blocked") {
    return "Your browser blocked the sign-in popup. Allow popups and try again.";
  }
  return err?.message || "Something went wrong. Please try again.";
}

export default function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [selectedSlug, setSelectedSlug] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthLoading(false);
    });
    return unsub;
  }, []);

  const handleSignIn = async () => {
    setError("");
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: "select_account" });
      await signInWithPopup(auth, provider);
    } catch (err) {
      setError(formatError(err));
    }
  };

  const handleSignOut = async () => {
    setError("");
    setSelectedSlug("");
    setRecaptchaToken(null);
    setAgreed(false);
    setSubmitted(null);
    await signOut(auth);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!user) return;
    if (!user.emailVerified) {
      setError("Your Google account email is not verified. Please verify it and try again.");
      return;
    }
    if (!selectedSlug || !recaptchaToken || !agreed) return;

    const app = APPS.find((a) => a.slug === selectedSlug);
    if (!app) {
      setError("Please choose a valid app.");
      return;
    }

    setSubmitting(true);
    const ymd = todayUtcYmd();
    const docId = `${user.uid}_${app.slug}_${ymd}`;
    try {
      await setDoc(doc(db, "deletionRequests", docId), {
        uid: user.uid,
        email: user.email,
        emailVerified: user.emailVerified,
        appSlug: app.slug,
        appName: app.name,
        date: ymd,
        recaptchaToken,
        userAgent: navigator.userAgent.slice(0, 500),
        createdAt: serverTimestamp(),
      });
      setSubmitted({ appName: app.name, email: user.email });
    } catch (err) {
      setError(formatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page">
      <main className="card" aria-labelledby="heading">
        <header className="card-header">
          <h1 id="heading">Account Data Deletion Request</h1>
          <p className="lede">
            Submit a verified request to delete your profile and associated data
            from one of our mobile apps.
          </p>
        </header>

        {authLoading ? (
          <p className="muted" role="status">Loading…</p>
        ) : submitted ? (
          <SubmittedPanel
            appName={submitted.appName}
            email={submitted.email}
            onDone={handleSignOut}
          />
        ) : !user ? (
          <SignInPanel onSignIn={handleSignIn} />
        ) : (
          <RequestForm
            user={user}
            selectedSlug={selectedSlug}
            onSelect={setSelectedSlug}
            recaptchaToken={recaptchaToken}
            onRecaptchaChange={setRecaptchaToken}
            agreed={agreed}
            onAgree={setAgreed}
            submitting={submitting}
            onSubmit={handleSubmit}
            onSignOut={handleSignOut}
          />
        )}

        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}

        <footer className="card-footer">
          <small>
            We verify your identity with Google before accepting a request. We
            only delete data tied to your signed-in email address.
          </small>
        </footer>
      </main>
    </div>
  );
}

function SignInPanel({ onSignIn }) {
  return (
    <section className="panel">
      <h2>Step 1 — Verify your identity</h2>
      <p>
        Sign in with the Google account you used in the app. We use this to
        confirm you own the data being deleted.
      </p>
      <button
        type="button"
        className="btn btn-google"
        onClick={onSignIn}
      >
        <img
          src="https://developers.google.com/identity/images/g-logo.png"
          alt=""
          width="18"
          height="18"
        />
        <span>Sign in with Google</span>
      </button>
    </section>
  );
}

function RequestForm({
  user,
  selectedSlug,
  onSelect,
  recaptchaToken,
  onRecaptchaChange,
  agreed,
  onAgree,
  submitting,
  onSubmit,
  onSignOut,
}) {
  const canSubmit =
    !!selectedSlug && !!recaptchaToken && agreed && !submitting;

  return (
    <section className="panel">
      <div className="identity">
        <div>
          <span className="muted">Signed in as</span>
          <strong>{user.email}</strong>
        </div>
        <button type="button" className="btn-link" onClick={onSignOut}>
          Switch account
        </button>
      </div>

      <form onSubmit={onSubmit} noValidate>
        <div className="field">
          <label htmlFor="app-select">
            Which app do you want to delete your profile from?
          </label>
          <select
            id="app-select"
            value={selectedSlug}
            onChange={(e) => onSelect(e.target.value)}
            required
          >
            <option value="">— Choose an app —</option>
            {APPS.map((a) => (
              <option key={a.slug} value={a.slug}>
                {a.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field notice">
          <p>
            <strong>What will be deleted:</strong> your profile, saved
            preferences, and records linked to <em>{user.email}</em> in the
            selected app. Aggregated, anonymised analytics that no longer
            identify you may be retained.
          </p>
          <p>
            <strong>Timeline:</strong> requests are processed within 30 days.
            You'll receive an email confirmation when the deletion is complete.
          </p>
        </div>

        <div className="field">
          <label className="checkbox">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => onAgree(e.target.checked)}
              required
            />
            <span>
              I confirm I'm the owner of <strong>{user.email}</strong> and I
              want to permanently delete the data linked to this account.
            </span>
          </label>
        </div>

        <div className="field recaptcha">
          <ReCAPTCHA
            sitekey={RECAPTCHA_SITE_KEY}
            onChange={onRecaptchaChange}
            onExpired={() => onRecaptchaChange(null)}
          />
        </div>

        <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
          {submitting ? "Submitting…" : "Submit deletion request"}
        </button>
      </form>
    </section>
  );
}

function SubmittedPanel({ appName, email, onDone }) {
  return (
    <section className="panel panel-success" aria-live="polite">
      <h2>Request received</h2>
      <p>
        We've recorded your request to delete data for <strong>{appName}</strong>{" "}
        linked to <strong>{email}</strong>. We'll process it within 30 days and
        email you a confirmation.
      </p>
      <p className="muted">
        You don't need to submit again — duplicate requests are ignored.
      </p>
      <button type="button" className="btn" onClick={onDone}>
        Done
      </button>
    </section>
  );
}
